import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { fetchSystemStatus } from "@/lib/api";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import AuditView from "@/pages/AuditView";
import Ledger from "@/pages/Ledger";
import Verify from "@/pages/Verify";
import Registry from "@/pages/Registry";
import Leaderboard from "@/pages/Leaderboard";
import Regulator from "@/pages/Regulator";
import Pricing from "@/pages/Pricing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import AuthCallback from "@/pages/AuthCallback";
import MaintenanceScreen from "@/pages/Maintenance";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-black text-[#808080] mono text-xs p-8 ae-cursor">CHECKING SESSION</div>;
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function RouterShell() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/verify" element={<Verify />} />
      <Route path="/registry" element={<Registry />} />
      <Route path="/leaderboard" element={<Leaderboard />} />
      <Route path="/regulator" element={<Regulator />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/ledger" element={<Protected><Ledger /></Protected>} />
      <Route path="/audits/:id" element={<Protected><AuditView /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function MaintenanceGate({ children }) {
  const [status, setStatus] = useState({ loading: true, maintenance: false, owner_ok: false });

  useEffect(() => {
    let mounted = true;
    (async () => {
      const s = await fetchSystemStatus();
      if (mounted) setStatus({ loading: false, ...s });
    })();
    // Poll every 30s so the screen self-heals when the owner disables maintenance.
    const iv = setInterval(async () => {
      const s = await fetchSystemStatus();
      if (mounted) setStatus({ loading: false, ...s });
    }, 30000);
    return () => { mounted = false; clearInterval(iv); };
  }, []);

  if (status.loading) {
    return <div className="min-h-screen bg-black text-[#808080] mono text-xs p-8 ae-cursor" data-testid="maintenance-probing">PROBING GATE</div>;
  }
  if (status.maintenance && !status.owner_ok) {
    return <MaintenanceScreen />;
  }
  return children;
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <MaintenanceGate>
            <RouterShell />
          </MaintenanceGate>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
